/**
 * Video Effects Store
 * Manages video effects state and syncs with server for user preferences
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import * as api from '../services/api';

export const useVideoEffectsStore = create(
  persist(
    (set, get) => ({
      // Effect state
      effectType: 'none', // 'none', 'blur', 'background'
      blurLevel: 'medium', // 'light', 'medium', 'heavy'
      selectedBackground: null, // URL of selected background
      customBackgrounds: [], // User-uploaded backgrounds

      // UI state
      effectsPanelOpen: false,
      isProcessing: false,
      isSupported: true, // WebGL support
      isInitialized: false,
      isSyncing: false,

      // Preset backgrounds
      presetBackgrounds: [
        { id: 'office', name: 'Ufficio', url: '/backgrounds/office.svg' },
        { id: 'beach', name: 'Spiaggia', url: '/backgrounds/beach.svg' },
        { id: 'nature', name: 'Natura', url: '/backgrounds/nature.svg' },
        { id: 'abstract', name: 'Astratto', url: '/backgrounds/abstract.svg' },
      ],

      // Actions
      setEffectType: (type) => {
        set({ effectType: type });
        get().syncToServer();
      },

      setBlurLevel: (level) => {
        set({ blurLevel: level });
        get().syncToServer();
      },

      setSelectedBackground: (url) => {
        set({ selectedBackground: url });
        get().syncToServer();
      },

      setEffectsPanelOpen: (open) => set({ effectsPanelOpen: open }),
      setIsProcessing: (processing) => set({ isProcessing: processing }),
      setIsSupported: (supported) => set({ isSupported: supported }),
      setIsInitialized: (initialized) => set({ isInitialized: initialized }),

      addCustomBackground: (url) => {
        const newBg = { id: `custom-${Date.now()}`, name: 'Custom', url };
        set((state) => ({
          customBackgrounds: [...state.customBackgrounds, newBg],
        }));
        get().syncToServer();
        return newBg;
      },

      removeCustomBackground: (id) => {
        set((state) => {
          const removed = state.customBackgrounds.find((bg) => bg.id === id);
          const newCustomBackgrounds = state.customBackgrounds.filter((bg) => bg.id !== id);
          // If the removed background was selected, clear selection
          const newSelectedBackground =
            state.selectedBackground === removed?.url ? null : state.selectedBackground;
          return {
            customBackgrounds: newCustomBackgrounds,
            selectedBackground: newSelectedBackground,
          };
        });
        get().syncToServer();
      },

      // Reset effect to none
      resetEffect: () => {
        set({ effectType: 'none', selectedBackground: null });
        get().syncToServer();
      },

      // Load preferences from server response (on login)
      loadFromServer: (videoPreferences) => {
        if (!videoPreferences) return;
        set({
          effectType: videoPreferences.effectType || 'none',
          blurLevel: videoPreferences.blurLevel || 'medium',
          selectedBackground: videoPreferences.backgroundUrl || null,
          customBackgrounds: (videoPreferences.customBackgrounds || []).map((url, i) => ({
            id: `custom-${i}`,
            name: 'Custom',
            url,
          })),
          isInitialized: true,
        });
      },

      // Fetch preferences from server (on demand)
      fetchFromServer: async (token) => {
        try {
          const response = await api.getVideoPreferences(token);
          get().loadFromServer(response.videoPreferences);
        } catch (error) {
          console.error('[VideoEffectsStore] Failed to fetch preferences:', error);
        }
      },

      // Sync current state to server (debounced)
      syncToServer: (() => {
        let timeoutId = null;
        return () => {
          if (timeoutId) clearTimeout(timeoutId);
          timeoutId = setTimeout(async () => {
            const state = get();
            if (state.isSyncing) return;

            // Get token from authStore
            const authStore = window.__authStore;
            const token = authStore?.getState?.()?.token;
            if (!token) return;

            set({ isSyncing: true });
            try {
              await api.updateVideoPreferences(
                {
                  effectType: state.effectType,
                  blurLevel: state.blurLevel,
                  backgroundUrl: state.selectedBackground,
                  customBackgrounds: state.customBackgrounds.map((bg) => bg.url),
                },
                token
              );
              console.log('[VideoEffectsStore] Preferences synced to server');
            } catch (error) {
              console.error('[VideoEffectsStore] Failed to sync preferences:', error);
            } finally {
              set({ isSyncing: false });
            }
          }, 1000); // Debounce 1 second
        };
      })(),

      // Get all backgrounds (preset + custom)
      getAllBackgrounds: () => {
        const state = get();
        return [...state.presetBackgrounds, ...state.customBackgrounds];
      },
    }),
    {
      name: 'video-effects-storage',
      // Only persist these fields to localStorage (as backup)
      partialize: (state) => ({
        effectType: state.effectType,
        blurLevel: state.blurLevel,
        selectedBackground: state.selectedBackground,
        customBackgrounds: state.customBackgrounds,
      }),
    }
  )
);

// Helper to connect auth store for syncing
export function connectAuthStore(authStore) {
  window.__authStore = authStore;
}

export default useVideoEffectsStore;
